/**
 * Owner Portal "My properties" grid — card for a property the owner
 * submitted themselves that isn't live yet (properties.approval_status
 * pending/rejected). Mirrors OwnerPropertyCard's shell but swaps the
 * image/stats body for approval status + Edit/Withdraw.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, AlertTriangle, Pencil, X, FileSignature } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { useWithdrawPropertySubmission } from "@/hooks/v2/usePropertySubmissions";
import {
  useStartOwnerContract,
  type StartContractError,
} from "@/hooks/owner/useStartOwnerContract";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";

type Props = {
  propertyId: number;
  title: string;
  city: string;
  approvalStatus: "pending" | "rejected";
  /**
   * Whether the owner's own next move is to sign — decided by the caller from
   * owner_visible_properties' approval/onboarding axes (ownerMustSign), not
   * from a derived state string. owner_start_contract applies the same rule
   * server-side, so the button is never offered for a call that fails.
   *
   * A submission keeps this card either way, because Edit and Withdraw live
   * here and an owner must not lose them just because onboarding is now asked
   * for before approval (two-axis port, 20261021190000).
   */
  mustSign?: boolean;
  submittedAt: string | null;
  reviewNote: string | null;
};

/**
 * What to tell the owner when the sign button fails, by reason.
 *
 * Every non-"already started" cause used to collapse into one generic
 * "that didn't work, try again" line -- which looks identical whether the
 * account has no email on file, the property no longer belongs to this
 * session, or something genuinely broke server-side. An owner (or whoever is
 * reading a screenshot of one) could not tell which. Each reason now gets its
 * own line and, where there's something the owner can actually do about it,
 * its own instruction.
 */
function signErrorCopy(
  error: StartContractError | null,
  t: (key: string, fallback?: string) => string
): string {
  switch (error) {
    case "not_awaiting_contract":
      return t(
        "owner.card.signAlreadyStarted",
        "Dieser Vertrag läuft bereits – laden Sie die Seite neu."
      );
    case "no_email_on_account":
      return t(
        "owner.card.signNoEmail",
        "Für Ihr Konto ist keine E-Mail-Adresse hinterlegt. Bitte melden Sie sich bei uns, damit wir das ergänzen können."
      );
    case "not_your_property":
      return t(
        "owner.card.signNotYourProperty",
        "Diese Immobilie konnte gerade nicht zugeordnet werden. Laden Sie die Seite neu; falls das bestehen bleibt, melden Sie sich bei uns."
      );
    default:
      return t(
        "owner.card.signFailed",
        "Das hat nicht geklappt. Bitte versuchen Sie es erneut."
      );
  }
}

export function PendingPropertyCard({
  propertyId,
  title,
  city,
  approvalStatus,
  mustSign = false,
  submittedAt,
  reviewNote,
}: Props) {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const withdrawMut = useWithdrawPropertySubmission();
  const start = useStartOwnerContract();
  // approvalStatus is belt-and-braces: ownerMustSign already excludes a
  // rejected property, and owner_start_contract refuses one regardless.
  const canSign = mustSign && approvalStatus !== "rejected";
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState("");

  /* Date AND time. A submission made this morning and one made a week ago
     both read "Submitted on 28 Jul 2026" with a date alone, and the owner's
     next question — after a day of silence — is exactly "when did I send it".
     toLocaleString rather than a second formatter so the separator between
     date and time stays the locale's own. */
  const submittedLabel = submittedAt
    ? new Date(submittedAt).toLocaleString(
        language === "de" ? "de-DE" : "en-GB",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }
      )
    : null;

  function withdraw() {
    const reason = withdrawReason.trim();
    if (!reason) return;
    withdrawMut.mutate(
      { propertyId, reason },
      {
        onSuccess: () => {
          setShowWithdraw(false);
          setWithdrawReason("");
        },
        onError: (err) =>
          toast({
            title: t("owner.card.withdrawError", "Could not withdraw"),
            description: err instanceof Error ? err.message : "Unknown error",
            variant: "destructive",
          }),
      }
    );
  }

  return (
    <>
      {/* A ghost of a normal property card sits underneath, blurred out: the
          tile keeps the grid's rhythm and reads as "this will become a real
          card once it's approved", rather than as a short notice floating in a
          gap. It is decorative only — aria-hidden, no real data. */}
      <div className="relative rounded-2xl overflow-hidden border border-dashed border-gray-300 bg-white/70">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 select-none blur-[3px] opacity-40"
        >
          <div className="h-36 bg-gray-100" />
          <div className="p-4 space-y-2">
            <div className="h-4 w-2/3 rounded bg-gray-200" />
            <div className="h-3 w-4/5 rounded bg-gray-100" />
            <div className="flex gap-6 pt-2">
              <div className="h-6 w-10 rounded bg-gray-100" />
              <div className="h-6 w-10 rounded bg-gray-100" />
              <div className="h-6 w-10 rounded bg-gray-100" />
            </div>
          </div>
        </div>
        <div className="relative flex flex-col items-center justify-center gap-3 px-6 pt-8 pb-6 text-center">
          {/* Same tag the contract-gated card carries, so every property that
              isn't live yet reads as one state to the owner. It is dropped
              while the property is purely waiting on coliviq's review: the
              owner has finished onboarding, so "onboarding pending" would
              contradict the "Awaiting coliviq Admin approval" line below. */}
          {(canSign || approvalStatus === "rejected") && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white shadow-sm">
              <Clock className="h-3.5 w-3.5" />
              {t("owner.card.yetToBeOnboarded", "Yet to be onboarded")}
            </span>
          )}
          <div
            className={
              approvalStatus === "rejected"
                ? "h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center"
                : canSign
                  ? "h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center"
                  : "h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center"
            }
          >
            {approvalStatus === "rejected" ? (
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            ) : canSign ? (
              <FileSignature className="h-5 w-5 text-teal-600" />
            ) : (
              <Clock className="h-5 w-5 text-orange-500" />
            )}
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-sm">
              {/* Since the two-axis port, onboarding comes BEFORE review, so a
                  pending property can be waiting on the OWNER. Saying
                  "Awaiting coliviq Admin approval" directly above a "Sign
                  contract now" button tells them in one breath that we are
                  reviewing and that they must act. */}
              {approvalStatus === "rejected"
                ? t("owner.card.changesRequested", "Changes requested")
                : canSign
                  ? t(
                      "owner.card.completeOnboarding",
                      "Complete your onboarding"
                    )
                  : t(
                      "owner.card.awaitingApproval",
                      "Awaiting coliviq Admin approval"
                    )}
            </p>
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
              {title} · {city}
            </p>
            {submittedLabel && (
              <p className="text-xs text-gray-400 mt-1">
                {t("owner.card.submittedOn", "Submitted on")} {submittedLabel}
              </p>
            )}
            {approvalStatus === "rejected" && reviewNote && (
              <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1.5 mt-2 text-left">
                {reviewNote}
              </p>
            )}
          </div>
          {canSign && (
            <div className="mt-1 flex flex-col gap-1">
              <button
                type="button"
                onClick={() => start.mutate(propertyId)}
                disabled={start.isPending}
                className="inline-flex w-fit items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition-colors disabled:opacity-60"
                style={{ background: "#2FB3AA", color: "#06201F" }}
              >
                {t("owner.card.signContractNow", "Sign contract now")} →
              </button>
              {start.isError && (
                <p
                  className="text-[11px]"
                  style={{ color: "#8A6B54" }}
                  role="alert"
                >
                  {signErrorCopy(start.error, t)}
                </p>
              )}
            </div>
          )}
          <div className="flex gap-2 mt-1">
            <button
              type="button"
              onClick={() =>
                navigate(`/admin/properties/new?edit=${propertyId}`)
              }
              className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              <Pencil className="h-3.5 w-3.5" />
              {t("owner.card.edit", "Edit")}
            </button>
            <button
              type="button"
              onClick={() => setShowWithdraw(true)}
              className="inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold text-gray-500 hover:bg-gray-50"
            >
              {t("owner.card.withdraw", "Withdraw")}
            </button>
          </div>
        </div>
      </div>

      <AlertDialog open={showWithdraw} onOpenChange={setShowWithdraw}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("owner.card.withdrawTitle", "Withdraw this submission?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "owner.card.withdrawDesc",
                "The submission is taken out of review and stops showing in your properties. coliviq keeps a record of what you submitted, so you can ask us to pick it back up."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-1.5">
            <label
              htmlFor="withdraw-reason"
              className="text-sm font-medium leading-none"
            >
              {t("owner.card.withdrawReasonLabel", "Reason for withdrawing")}
            </label>
            <Textarea
              id="withdraw-reason"
              value={withdrawReason}
              onChange={(e) => setWithdrawReason(e.target.value)}
              rows={3}
              placeholder={t(
                "owner.card.withdrawReasonPlaceholder",
                "e.g. The unit is let privately for now — I'll resubmit in the spring."
              )}
            />
            <p className="text-xs text-muted-foreground">
              {t(
                "owner.card.withdrawReasonHint",
                "Required — this is what the coliviq team sees."
              )}
            </p>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("owner.card.withdrawCancel", "Keep it")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // Radix closes the dialog on action-click by default; an empty
                // reason must keep it open instead of silently doing nothing.
                if (!withdrawReason.trim()) {
                  e.preventDefault();
                  return;
                }
                withdraw();
              }}
              disabled={withdrawMut.isPending || !withdrawReason.trim()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <X className="mr-1.5 h-3.5 w-3.5" />
              {t("owner.card.withdrawConfirm", "Withdraw")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
